import { useCallback, useEffect, useRef, useState } from 'react';
import { CollectionView } from 'common';
import { CollectionsListState } from '@src/types/Collection';


interface UseCategoryCollapseArgs {
  collectionsList: CollectionsListState;
  visibleCollectionsByCategory: Map<string, CollectionView[]>;
  isDragging: boolean;
  isLoading: boolean;
}

export function useCategoryCollapse({
  collectionsList,
  visibleCollectionsByCategory,
  isDragging,
  isLoading,
}: UseCategoryCollapseArgs) {
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const autoCollapsedEmptyCategories = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (isDragging) {
      return;
    }
    setCollapsedCategories((prev) => {
      const validNames = new Set(collectionsList.map((cat) => cat.name));
      const next = new Set<string>();
      let changed = false;

      prev.forEach((name) => {
        if (validNames.has(name)) {
          next.add(name);
        } else {
          changed = true;
        }
      });

      const updatedAutoCollapsed = new Set<string>();
      autoCollapsedEmptyCategories.current.forEach((name) => {
        if (validNames.has(name)) {
          updatedAutoCollapsed.add(name);
        }
      });

      collectionsList.forEach((cat) => {
        const shouldAutoCollapse = cat.list.length === 0 && !updatedAutoCollapsed.has(cat.name);
        if (shouldAutoCollapse) {
          updatedAutoCollapsed.add(cat.name);
          if (!next.has(cat.name)) {
            next.add(cat.name);
            changed = true;
          }
        }
      });

      autoCollapsedEmptyCategories.current = updatedAutoCollapsed;

      if (!changed && prev.size === next.size) {
        return prev;
      }
      return next;
    });
  }, [collectionsList, isDragging]);

  useEffect(() => {
    if (isLoading || isDragging) {
      return;
    }
    setCollapsedCategories((prev) => {
      let changed = false;
      const next = new Set(prev);
      collectionsList.forEach((category) => {
        const visible = visibleCollectionsByCategory.get(category.name) ?? [];
        if (visible.length > 0 && next.has(category.name)) {
          next.delete(category.name);
          changed = true;
        }
        if (visible.length === 0 && !next.has(category.name)) {
          next.add(category.name);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [collectionsList, visibleCollectionsByCategory, isLoading, isDragging]);

  const toggleCategory = useCallback((categoryName: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryName)) {
        next.delete(categoryName);
      } else {
        next.add(categoryName);
      }
      return next;
    });
  }, []);

  return { collapsedCategories, toggleCategory };
}
